/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { inject, Injectable } from '@angular/core';
import { OidcService } from '@irian-ro/auth-oidc';
import {
  getAllCompanyListRights,
  getCRMRights,
  getEmailAuthRights,
  getSAFTRights,
  getSelectItemsRights,
  Rights
} from '@well/rights';
import { Router } from '@angular/router';
import { map, switchMap } from 'rxjs/operators';
import { ContextService } from './context.service';
import { translationSchema } from '@well/translation';
import { TranslateService } from '@ngx-translate/core';
import { merge, of } from 'rxjs';
import { WellMenuItem } from './well-menu-item.model';
import { NavigationService } from '@well/navigation';
import { LoggedInContext, UserFacade } from '@well/shared-providers';

@Injectable({
  providedIn: 'root',
})
export class MainMenuItemsService {
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private navigationService = inject(NavigationService);

  private readonly contextService = inject(ContextService);

  private readonly userFacade = inject(UserFacade);

  labels = {
    crmLabel: translationSchema['shell-app']['navigationMenu']['crm'],
    deliveryLabel: translationSchema['shell-app']['navigationMenu']['delivery'],
    myCompanyLabel: translationSchema['shell-app']['navigationMenu']['myCompany'],
    selectItemsLabel: translationSchema['shell-app']['navigationMenu']['selectItems'],
    allCompaniesLabel: translationSchema['shell-app']['navigationMenu']['allCompanies'],
    logoutLabel: translationSchema['shell-app']['navigationMenu']['logout'],
    saftLabel: translationSchema['shell-app']['navigationMenu']['saftLabel'],
  };

  private readonly oidcService = inject(OidcService);

  getMenu() {
    return merge(
      this.contextService.onUserChange(),
      this.oidcService.onAuthenticated.pipe(map(() => null))
    ).pipe(
      switchMap((context) => {
        if (context && context.loggedInUser && context.loggedInUser.name) {
          return this.translateService.stream(Object.values(translationSchema['shell-app']['navigationMenu'])).pipe(
            map((res) => {
              const menuItems: WellMenuItem[] = [];

              const hasSignupRole = context.loggedInUser?.authorities?.includes(Rights.ROLE_SIGNUP);

              if (hasSignupRole) {
                menuItems.push({
                  id: 'logoutToolbarButton',
                  label: res[this.labels.logoutLabel],
                  command: () =>
                    this.router
                      .navigate([{ outlets: { 'popup-full': null } }])
                      .then(() => {
                        this.oidcService.logout();
                      }),
                });
              } else {
                // Scoped magic-link sessions get no full menu (centralised check — was a
                // local copy of the authType whitelist).
                if (!this.userFacade.isScopedSession()) {
                  menuItems.push({
                    id: 'crmToolbarButton',
                    label: res[this.labels.crmLabel],
                    icon: 'ui-icon-pie-chart',
                    command: () => this.redirect('crm'),
                    rights: getCRMRights(),
                  });

                  if (context.loggedInUser?.authType === 'authEmail') {
                    menuItems.push({
                      id: 'deliveryToolbarButton',
                      label: res[this.labels.deliveryLabel],
                      icon: 'ui-icon-near-me',
                      command: () => this.navigateByRights(context),
                      rights: getEmailAuthRights(),
                    });
                  } else {
                    menuItems.push({
                      id: 'deliveryToolbarButton',
                      label: res[this.labels.deliveryLabel],
                      icon: 'ui-icon-near-me',
                      command: () => this.navigateByRights(context),
                    });
                  }

                  menuItems.push({
                    id: 'myCompanyToolbarButton',
                    label: res[this.labels.myCompanyLabel],
                    icon: 'ui-icon-business',
                    command: () => this.redirect('company'),
                  });
                  menuItems.push({
                    id: 'allCompaniesToolbarButton',
                    label: res[this.labels.allCompaniesLabel],
                    icon: 'ui-icon-device-hub',
                    command: () => this.redirect('company/public/list'),
                    rights: [getAllCompanyListRights(), getSelectItemsRights()],
                  });
                  menuItems.push({
                    id: 'selectItemsToolbarButton',
                    label: res[this.labels.selectItemsLabel],
                    command: () => this.redirect('crm/select-items'),
                    rights: getSelectItemsRights(),
                  });

                  menuItems.push({
                    id: 'saftToolbarButton',
                    label: res[this.labels.saftLabel],
                    command: () => this.redirect('saft'),
                    rights: getSAFTRights(),
                  });
                }

                menuItems.push({
                  id: 'logoutToolbarButton',
                  label: res[this.labels.logoutLabel],
                  command: () =>
                    this.router
                      .navigate([{ outlets: { 'popup-full': null } }])
                      .then(() => {
                        this.oidcService.logout();
                      }),
                });
              }

              return menuItems;
            })
          );
        }

        if (this.oidcService.isAuthenticated) {
          return this.translateService.stream(Object.values(translationSchema['shell-app']['navigationMenu'])).pipe(
            map((res) => [{
              id: 'logoutToolbarButton',
              label: res[this.labels.logoutLabel],
              command: () =>
                this.router
                  .navigate([{ outlets: { 'popup-full': null } }])
                  .then(() => {
                    this.oidcService.logout();
                  }),
            } as WellMenuItem])
          );
        }

        return of([]);
      })
    );
  }

  redirect(path: string) {
    this.router.navigate([{ outlets: { 'popup-full': null } }]).then(() => this.router.navigate([`/${path}`]));
  }

  navigateByRights(context: LoggedInContext) {
    this.router
      .navigate([{ outlets: { 'popup-full': null } }])
      .then(() => this.navigationService.navigateByRights(context));
  }
}
